// What A/B test this tab is in, said in words, with a way to switch.
//
// The rollout system is deliberately invisible to players — a stable bucket, a
// locked answer, nothing on screen — which for the people *building* the arms
// is the problem: nothing distinguishes "I am on the five-turn wave delay" from
// the current game, and looking at the other arm meant hashing a string by
// hand. This is the developer's answer to both questions at once: a chip that
// names every test in play and which version this tab is seeing, and buttons
// that switch.
//
// **The buttons hold no override of their own — they write the `?flag=` URL
// and reload.** The URL was already the sanctioned per-tab override
// (`visitor.ts`), so this is a hand that types it rather than a second
// mechanism that could disagree with the first: the result is still a link you
// can paste to a teammate, and `useFlag`'s lock (an arm must not change under a
// mounted game) is respected because nothing changes until the reload.
//
// Gated by `useDevTools` — the build fact locally, the account's role on the
// deployment — so players never see it. **It does not place itself**: each
// client reads it from `ExperimentsBadgeContext` and renders it in its own dev
// corner, which must be out of flow for `DevMenu`'s reason — nothing in the
// shell may change size with it, or the canvas reflows and throws away a frame.

import { useEffect, useRef, useState } from "react";

import { type FlagStanding, useFlagStandings } from "src/client/ai/hooks/useFlag";
import { Btn, Divider, HoverCard, Panel, Pill, PillLabel, styled, Text } from "./kit";
import { useDevTools } from "./useDevTools";

/** "wave-delay" said like a person would: "Wave delay". */
const titleOf = (id: string): string => {
  const words = id.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The versions a flag can be switched between, lettered the way people talk
 * about a test: **A is always the control** — the current game — and the arms
 * are B, C, … in band order. A boolean flag is a two-version test where B is
 * "on".
 */
interface Version {
  letter: string;
  /** What this version is, in the flag's own vocabulary. */
  word: string;
  /** The `?flag=` entry that forces it. */
  entry: string;
}

const versionsOf = (flag: FlagStanding): Version[] => {
  if (flag.variants && flag.variants.length > 0) {
    return [
      { letter: "A", word: "current", entry: `-${flag.id}` },
      ...flag.variants.map((arm, i) => ({
        letter: String.fromCharCode(66 + i),
        word: String(arm),
        entry: `${flag.id}=${String(arm)}`
      }))
    ];
  }
  return [
    { letter: "A", word: "off", entry: `-${flag.id}` },
    { letter: "B", word: "on", entry: flag.id }
  ];
};

/** The letter of the version this tab is actually seeing. */
const seeing = (flag: FlagStanding): Version => {
  const versions = versionsOf(flag);
  if (flag.variants && flag.variants.length > 0) {
    const at = flag.variants.findIndex((arm) => arm === flag.arm);
    return at >= 0 ? versions[at + 1] : versions[0];
  }
  return flag.on ? versions[1] : versions[0];
};

/**
 * Rewrite this tab's `?flag=` entries for one flag and reload.
 *
 * Every other query parameter — `?room=`, `?pack=`, other flags' overrides —
 * is kept, because the URL is state other systems are already using. `null`
 * clears the flag's own entries, which is what "Auto" means: back to whatever
 * the bucket assigns.
 */
const applyForce = (id: string, entry: string | null): void => {
  const url = new URL(window.location.href);
  const kept = url.searchParams
    .getAll("flag")
    .filter((held) => held !== id && held !== `-${id}` && !held.startsWith(`${id}=`));
  url.searchParams.delete("flag");
  for (const held of kept) url.searchParams.append("flag", held);
  if (entry !== null) url.searchParams.append("flag", entry);
  window.location.assign(url.toString());
};

/** The sheet above its button. Where that sits is the host's to say. */
const Corner = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: theme.kit.space.sm
}));

const Sheet = styled(Panel)(({ theme }) => ({
  width: 340,
  boxSizing: "border-box",
  padding: theme.kit.space.md,
  maxHeight: "calc(100vh - 96px)",
  overflowY: "auto",
  boxShadow: `0 12px 32px color-mix(in srgb, ${theme.kit.color.shadow} 70%, transparent)`
}));

/** One flag's block. Drawn once per standing, so a `styled` rather than `sx`. */
const Entry = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs,
  marginBottom: theme.kit.space.md
}));

const Arms = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.kit.space.xs
}));

export function ExperimentsBadge(): React.ReactElement | null {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const devTools = useDevTools();
  const standings = useFlagStandings();

  // Click away to close, on pointerdown so it resolves before the board's own
  // click handler — the same pattern `DevMenu` uses.
  useEffect(() => {
    if (!open) return;
    const dismiss = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [open]);

  if (!devTools) return null;

  // A test is *in play* when this tab's experience can differ from somebody
  // else's: a multi-way flag with any audience, a partial rollout, or a forced
  // override. A flag sitting at 0 or 1 with no arms is configuration, not an
  // experiment, and does not earn a chip — though it is still listed in the
  // panel, because forcing one on is exactly how an unshipped arm gets looked
  // at.
  const running = standings.filter(
    (flag) =>
      flag.forced ||
      ((flag.variants?.length ?? 0) > 0 && flag.rollout > 0) ||
      (flag.rollout > 0 && flag.rollout < 1)
  );
  if (running.length === 0) return null;

  const label =
    running.length === 1
      ? `${titleOf(running[0].id)} — ${seeing(running[0]).letter} (${seeing(running[0]).word})` +
        (running[0].forced ? " · forced" : "")
      : `${running.length} tests running` +
        (running.some((flag) => flag.forced) ? " · forced" : "");

  return (
    <Corner ref={root}>
      {open && (
        <Sheet data-testid="experiments-panel">
          <PillLabel>A/B tests</PillLabel>
          <Divider />

          {standings.map((flag) => {
            const versions = versionsOf(flag);
            const now = seeing(flag);
            const share = Math.round(flag.rollout * 100);
            const standing = flag.forced
              ? "Forced for this tab by its URL — Auto goes back to your natural assignment."
              : flag.variants
                ? `Assigned by rollout: ${share}% of visitors are in this test, split evenly across its ${flag.variants.length} version${flag.variants.length > 1 ? "s" : ""}.`
                : `Rollout: on for ${share}% of visitors.`;
            return (
              <Entry key={flag.id}>
                {/* **The account rides on the card, not on the panel.** Some
                    `detail` entries run to a paragraph — `require-account`'s is
                    documentation — and this is a switchboard, not a reading
                    page: `/features` is where the same text is shown in full.
                    Left, because the panel is at the right edge of the window
                    and a column opens to the side (U6 rule 3). */}
                <HoverCard placement="left" title={titleOf(flag.id)} card={flag.detail}>
                  <Text block weight={700} size={12.5}>
                    {titleOf(flag.id)}
                  </Text>
                </HoverCard>
                <Pill>
                  <PillLabel>seeing</PillLabel>
                  <Text role="display" tone="brand" weight={700} size={12}>
                    {now.letter} · {now.word}
                  </Text>
                  {flag.forced && (
                    <Text tone="rust" weight={700} size={11}>
                      forced
                    </Text>
                  )}
                </Pill>
                <Text block tone="dim" size={11}>
                  {standing}
                </Text>
                <Arms>
                  {versions.map((version) => (
                    <Btn
                      key={version.letter}
                      small
                      variant={version.letter === now.letter ? "primary" : "default"}
                      data-testid={`exp-${flag.id}-${version.letter.toLowerCase()}`}
                      aria-pressed={version.letter === now.letter}
                      onClick={() => applyForce(flag.id, version.entry)}
                    >
                      {version.letter} · {version.word}
                    </Btn>
                  ))}
                  {/* **`bottom`, and deliberately not `beside`.** The panel
                      hugs the right edge of the window, so a card on its outer
                      edge has nowhere to go and flips back over the panel it
                      was meant to clear. `Auto` sits in a horizontal row, and a
                      row opens downward (U6 rule 3). */}
                  <HoverCard
                    placement="bottom"
                    title="Auto"
                    card="Clears the override and reloads — back to whatever the rollout assigns you."
                  >
                    <Btn
                      small
                      variant={flag.forced ? "default" : "cream"}
                      data-testid={`exp-${flag.id}-auto`}
                      aria-pressed={!flag.forced}
                      onClick={() => applyForce(flag.id, null)}
                    >
                      Auto
                    </Btn>
                  </HoverCard>
                </Arms>
              </Entry>
            );
          })}

          <Text block tone="dim" size={11}>
            Switching writes <code>?flag=</code> into the address bar and reloads, so it is
            shareable as a link and affects only this tab. A game in progress is lost by the
            reload; a run resumes from its save.
          </Text>
        </Sheet>
      )}

      <Btn
        small
        variant={open ? "primary" : "default"}
        data-testid="experiments-badge"
        aria-label="A/B tests"
        onClick={() => setOpen((was) => !was)}
      >
        🧪 {label}
      </Btn>
    </Corner>
  );
}
